
import GenerateJigFormSection from '@/components/modals/GenerateJigFormSection.vue'
import GenerateJigLoadingSection from '@/components/modals/GenerateJigLoadingSection.vue'
import GenerateJigPreviewSection from '@/components/modals/GenerateJigPreviewSection.vue'
import GenerateJigService, { GenerateJigProgress } from '@/services/GenerateJigService'
import { Tenant } from '@/store/modules/app/types'
import { LibraryEntry, LibraryReferenceAsset } from '@/store/modules/teamlibrary/types'
import { Namespace } from '@/store/types'
import { Timing } from '@/utils/timing'
import IconSparkColor from '@svg/Icon_Spark_Color.svg'
import IconClose from '@svg/IconClose.svg'
import { Component, Prop, Vue } from 'vue-property-decorator'
import { Action, Getter, State } from 'vuex-class'

declare var consoleError: any

type ModalView = 'form' | 'loading' | 'preview'

@Component({
  components: {
    IconClose,
    GenerateJigFormSection,
    GenerateJigLoadingSection,
    GenerateJigPreviewSection,
    IconSparkColor,
  },
})
export default class GenerateJigModal extends Vue {
  @Getter('isGlobalAIOptOut', { namespace: Namespace.App })
  public isGlobalAIOptOut!: boolean

  @State('myTenant', { namespace: Namespace.App })
  public myTenant!: Tenant

  @State('libraryReferenceAssets', { namespace: Namespace.TeamLibrary })
  public libraryReferenceAssets!: LibraryReferenceAsset[]
  @Action('loadLibraryReferenceAssets', { namespace: Namespace.TeamLibrary })
  public loadLibraryReferenceAssets!: (payload: { libraryModelId: number; limit?: number }) => Promise<boolean>

  @Prop({ type: Object, required: true })
  public libraryEntry!: LibraryEntry

  private dialogVisible: boolean = false
  private modalView: ModalView = 'form'
  private errorMessage: string = ''

  private previewUrl: string = ''
  private generatedJigId: string = ''
  private editUrl: string = ''
  private deeplinkUrl: string = ''
  private traceId: string = ''
  private feedbackUserId: string = ''
  private generationRunSequence: number = 0
  private activeGenerationRunId: number = 0
  private activeGenerationService: GenerateJigService | null = null
  private canCancelActiveRun: boolean = false
  private pendingDiscardService: GenerateJigService | null = null
  private showCompletionUnknownWarning: boolean = false
  private previewWarningCopy: string = ''

  private get feedbackTenantId(): number {
    return this.myTenant && typeof this.myTenant.ID === 'number' ? this.myTenant.ID : -1
  }

  private get dialogMaxWidth(): number {
    return this.modalView === 'preview' ? 980 : 750
  }

  private beforeDestroy() {
    void this.discardPendingGeneratedJig()

    const service = this.activeGenerationService
    const canCancelActiveRun = this.canCancelActiveRun

    this.clearActiveGenerationRun()

    if (service && canCancelActiveRun) {
      void service.cancel().catch((error) => {
        consoleError('Failed to cancel generate jig', error)
      })
    }
  }

  public show() {
    this.resetForm()
    this.dialogVisible = true
  }

  public hide() {
    this.dialogVisible = false
    this.resetForm()
  }

  private isCurrentRun(runId: number, service: GenerateJigService): boolean {
    return this.activeGenerationRunId === runId && this.activeGenerationService === service
  }

  private clearActiveGenerationRun(runId?: number) {
    if (runId != null && runId > 0 && this.activeGenerationRunId !== runId) {
      return
    }

    this.activeGenerationRunId = 0
    this.activeGenerationService = null
    this.canCancelActiveRun = false
  }

  private async discardPendingGeneratedJig() {
    const service = this.pendingDiscardService
    this.pendingDiscardService = null

    if (!service) {
      return
    }

    try {
      await service.discardGeneratedJig()
    } catch (error) {
      consoleError('Failed to discard unresolved generated Jig', error)
    }
  }

  private async cancelCurrentGeneration() {
    const service = this.activeGenerationService
    const runId = this.activeGenerationRunId

    this.clearActiveGenerationRun(runId)
    this.hide()

    if (service) {
      try {
        await service.cancel()
      } catch (error) {
        consoleError('Failed to cancel generate jig', error)
      }
    }
  }

  private updateLoadingProgress(progress: GenerateJigProgress) {
    const loadingSection = this.$refs.loadingSection as InstanceType<typeof GenerateJigLoadingSection> | undefined
    if (loadingSection) {
      loadingSection.updateProgress(progress)
    }
  }

  private getDisplayErrorMessage(error: Error | null | undefined): string {
    if (!error || !error.message) {
      return 'Something went wrong. Please try again.'
    }

    const message = error.message
    const exposesInternalPath =
      message.indexOf('/models/') >= 0 ||
      message.indexOf('ItemUrl') >= 0 ||
      message.indexOf('http://') >= 0 ||
      message.indexOf('https://') >= 0

    if (exposesInternalPath) {
      return 'This model could not be prepared for Jig generation. Please check the library item and try again.'
    }

    return message
  }

  private resetForm() {
    this.modalView = 'form'
    this.errorMessage = ''
    this.previewUrl = ''
    this.generatedJigId = ''
    this.editUrl = ''
    this.deeplinkUrl = ''
    this.traceId = ''
    this.feedbackUserId = ''
    this.showCompletionUnknownWarning = false
    this.previewWarningCopy = ''
  }

  private restartFromForm() {
    this.resetForm()
  }

  private async closeDialog() {
    if (this.modalView === 'loading') {
      if (this.canCancelActiveRun && this.activeGenerationService) {
        void this.cancelCurrentGeneration()
        return
      }

      this.clearActiveGenerationRun()
    }

    const shouldPreservePendingGeneratedJig = this.modalView === 'form' && this.pendingDiscardService != null
    this.hide()

    if (shouldPreservePendingGeneratedJig) {
      return
    }

    await this.discardPendingGeneratedJig()
  }

  private onClickOutside() {
    if (this.modalView === 'loading') {
      return
    }
    void this.closeDialog()
  }

  private shouldLoadPromptContext(useCase: string): boolean {
    return typeof useCase === 'string' && useCase.trim().length > 0
  }

  private getLibraryModelId(): number {
    return this.libraryEntry && this.libraryEntry.Item && typeof this.libraryEntry.Item.Id === 'number' ? this.libraryEntry.Item.Id : -1
  }

  private getReferenceAssetsForCurrentLibraryModel(libraryModelId: number): LibraryReferenceAsset[] {
    const refs = this.libraryReferenceAssets || []
    if (libraryModelId <= 0 || refs.length === 0) {
      return []
    }

    return refs.filter((asset: LibraryReferenceAsset) => asset && asset.libraryModelId === libraryModelId)
  }

  private async ensureLibraryReferenceAssetsReady(useCase: string): Promise<LibraryReferenceAsset[]> {
    if (!this.shouldLoadPromptContext(useCase)) {
      return []
    }

    const libraryModelId = this.getLibraryModelId()
    if (libraryModelId <= 0) {
      throw new Error('Library reference data is not ready yet.')
    }

    let matchingAssets = this.getReferenceAssetsForCurrentLibraryModel(libraryModelId)

    if (matchingAssets.length === 0) {
      this.updateLoadingProgress({
        message: 'Loading reference files...',
        percentage: 2,
      })

      const isLoaded = await this.loadLibraryReferenceAssets({
        libraryModelId: libraryModelId,
        limit: 999,
      })

      if (!isLoaded) {
        throw new Error('Could not load reference files for this library item.')
      }

      matchingAssets = this.getReferenceAssetsForCurrentLibraryModel(libraryModelId)
    }

    return matchingAssets
  }

  private async generateJig(formData: { description: string; name: string; useCase: string; useCaseLabel: string }) {
    await this.discardPendingGeneratedJig()
    this.errorMessage = ''
    this.showCompletionUnknownWarning = false
    this.previewWarningCopy = ''

    if (!this.libraryEntry || !this.libraryEntry.Item) {
      this.errorMessage = 'Library entry data is not ready yet.'
      return
    }

    const tenantId = this.myTenant && typeof this.myTenant.ID === 'number' ? this.myTenant.ID : -1
    if (tenantId <= 0) {
      this.errorMessage = 'Could not resolve tenant ID for this session.'
      return
    }

    let libraryModelReferences: LibraryReferenceAsset[] = []

    try {
      libraryModelReferences = await this.ensureLibraryReferenceAssetsReady(formData.useCase)
    } catch (error) {
      const err = error as Error
      this.errorMessage = err && err.message ? err.message : 'Could not prepare context for Jig generation.'
      return
    }

    const service = new GenerateJigService(tenantId)
    const runId = ++this.generationRunSequence
    this.activeGenerationRunId = runId
    this.activeGenerationService = service
    this.canCancelActiveRun = true
    this.modalView = 'loading'
    this.updateLoadingProgress({
      message: 'Starting Jig generation...',
      percentage: 0,
    })

    try {
      const result = await service.generateJig(
        {
          libraryEntry: this.libraryEntry,
          userQuestion: formData.description,
          jigName: formData.name,
          jigUseCase: formData.useCaseLabel,
          jigUseCaseTemplateName: formData.useCase,
          libraryModelReferences: libraryModelReferences,
          tenantName: this.myTenant && this.myTenant.Name ? this.myTenant.Name : '',
        },
        (progress: GenerateJigProgress) => {
          if (this.isCurrentRun(runId, service)) {
            this.updateLoadingProgress(progress)
          }
        }
      )

      if (!this.isCurrentRun(runId, service)) {
        return
      }

      // From this point the generated Jig should no longer be deleted by cancel.
      service.commitGeneratedJig()
      this.canCancelActiveRun = false
      this.pendingDiscardService = null

      this.updateLoadingProgress({
        message: 'Jig created successfully.',
        percentage: 100,
      })
      await Timing.sleep(260)

      if (!this.isCurrentRun(runId, service)) {
        return
      }

      this.generatedJigId = result.jigId
      this.previewUrl = result.previewURL
      this.editUrl = result.editURL
      this.deeplinkUrl = result.deeplinkURL
      this.traceId = result.traceId || ''
      this.feedbackUserId = result.userId || ''
      this.previewWarningCopy = result.warningCopy || ''

      this.modalView = 'preview'
      this.clearActiveGenerationRun(runId)
    } catch (error) {
      if (!this.isCurrentRun(runId, service)) {
        return
      }

      this.clearActiveGenerationRun(runId)
      const err = error as Error
      this.showCompletionUnknownWarning = err && err.name === 'GenerateJigCompletionUnknownError'
      this.pendingDiscardService = this.showCompletionUnknownWarning ? service : null
      this.errorMessage = this.getDisplayErrorMessage(err)
      if (this.showCompletionUnknownWarning) {
        this.errorMessage = ''
      }
      this.modalView = 'form'
    }
  }
}
